import { CaretDownIcon, Icon as PhosphorIcon, XIcon } from '@phosphor-icons/react'
import React, { useRef } from 'react'

import { Badge } from '@/components/ui/badge'
import { Icon } from '@/components/ui/icon'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { QuantityBadge } from '@/components/ui/quantity-badge'

import { MAX_VISIBLE_BADGES } from './select.constants'
import * as S from './select.styled'
import { SelectOption, SelectOptionValue, SelectSize } from './select.types'
import { SelectTruncationTooltip } from './select-truncation-tooltip'

interface SelectTriggerProps<V extends SelectOptionValue> {
  customTrigger?: React.ReactNode
  disabled: boolean
  error?: string
  hasCustomTrigger: boolean
  isMulti: boolean
  isOpen: boolean
  isTreeCheckbox: boolean
  multipleSelectedLabel?: string
  placeholder: string
  referenceProps: Record<string, unknown>
  selectedCount: number
  selectedOptions: Array<SelectOption<V>>
  setReference: (node: HTMLElement | null) => void
  size: SelectSize
  icon?: PhosphorIcon
  loading?: boolean
  display?: 'default' | 'badges'
  /** Hides the caret while `disabled` is true - for a value shown read-only,
   * so it doesn't look like a dropdown the user could still open. */
  hideCaretOnDisable?: boolean
  readOnly: boolean
  onClear?: () => void
  onBadgeRemove?: (value: V) => void
  /** Maps the trigger to its `Label` (htmlFor) — defaults to `name`. */
  id?: string
  /** id(s) of the footer message, linked via `aria-describedby`. */
  describedBy?: string
  searchable: boolean
  searchQuery: string
  searchPlaceholder?: string
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSearchBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  /** Opens/closes the dropdown - the searchable trigger's caret toggles it explicitly. */
  onToggleOpen: (open: boolean) => void
}

export const SelectTrigger = <V extends SelectOptionValue>({
  customTrigger,
  disabled,
  error,
  hasCustomTrigger,
  isMulti,
  isOpen,
  isTreeCheckbox,
  multipleSelectedLabel,
  placeholder,
  referenceProps,
  selectedCount,
  selectedOptions,
  setReference,
  size,
  icon,
  loading,
  id,
  describedBy,
  display,
  hideCaretOnDisable = false,
  readOnly,
  onClear,
  onBadgeRemove,
  searchable,
  searchQuery,
  searchPlaceholder,
  onSearchChange,
  onSearchBlur,
  onToggleOpen,
}: SelectTriggerProps<V>) => {
  const searchInputRef = useRef<HTMLInputElement>(null)

  if (hasCustomTrigger) {
    return (
      <S.TriggerWrapper ref={setReference} {...referenceProps}>
        {customTrigger}
      </S.TriggerWrapper>
    )
  }

  const hasError = !!error
  const isBadgeMode = display === 'badges'
  const hasBadges = isBadgeMode && selectedOptions.length > 0
  const selectedStatus = !isMulti ? selectedOptions[0]?.status : undefined

  // Note: tree-checkbox mode shows the single selected leaf's name (unlike plain
  // `multi`, which always shows the placeholder - selections there can be long
  // flat lists not meant to fit inline) and a domain-specific label once 2+ are
  // selected, falling back to `placeholder` if the consumer didn't provide one.
  // Plain `multi` hides its quantity badge in badge-chip display mode - the chips
  // in BadgesArea already show the count implicitly.
  let canShowQuantityBadge: boolean
  let triggerLabel: string
  if (isTreeCheckbox) {
    canShowQuantityBadge = selectedCount > 0
    if (selectedCount === 0) {
      triggerLabel = placeholder
    } else if (selectedCount === 1) {
      triggerLabel = selectedOptions[0]?.label ?? placeholder
    } else {
      triggerLabel = multipleSelectedLabel ?? placeholder
    }
  } else if (isMulti) {
    canShowQuantityBadge = selectedCount > 0 && !isBadgeMode
    triggerLabel = placeholder
  } else {
    canShowQuantityBadge = false
    triggerLabel = selectedOptions[0]?.label ?? placeholder
  }

  // Note: read-only shows no placeholder - an empty read-only field is just blank.
  if (readOnly && selectedCount === 0) {
    triggerLabel = ''
  }

  // Note: the search input's `value` when closed - empty (so the native
  // `placeholder` attribute shows through, greyed) whenever the trigger would
  // otherwise render its own placeholder-colored text; filled with the real
  // label/count text otherwise. Mirrors the button branch's
  // `$isPlaceholder={!selectedCount}` coloring rule exactly.
  const closedSearchValue = selectedCount > 0 ? triggerLabel : ''

  const leadingSlot = icon && (
    // Note: identical leading slot to `TextInput` — icon sized by the box's
    // `svg` rule, followed by the shared divider.
    <S.LeadingSlot
      $size={size}
      $hasError={hasError}
      $isDisabled={disabled}
      $isReadOnly={readOnly}
      data-component-id="LeadingSlot"
    >
      <Icon icon={icon} />
      <S.Divider $hasError={hasError} $isDisabled={!!disabled} $isReadOnly={readOnly} />
    </S.LeadingSlot>
  )

  const badgesArea = hasBadges && (
    <S.BadgesArea>
      {selectedOptions.slice(0, MAX_VISIBLE_BADGES).map((option) => (
        <Badge
          key={String(option.value)}
          color={option.badgeColor ?? 'blue'}
          size="small"
          variant={option.badgeVariant ?? 'light'}
          removeIconSize="1.6rem"
          onRemove={isMulti ? () => onBadgeRemove?.(option.value) : undefined}
        >
          {option.label}
        </Badge>
      ))}
    </S.BadgesArea>
  )

  // Note: the selected count sits after the caret (design's trailing group). In
  // badges mode it's always visible (unlike the capped pills above) - a fixed
  // sibling of `BadgesArea`, not one of its wrapped children, so it never gets
  // pushed onto a second row.
  const trailingCount =
    hasBadges || canShowQuantityBadge ? (
      <QuantityBadge quantity={selectedCount} size="medium" />
    ) : null

  // Note: always rendered, always right-aligned, in every mode - it no longer
  // hides behind a multi-select's quantity badge - except when `hideCaretOnDisable`
  // is set and the field is disabled, for a value shown read-only that shouldn't
  // look like a dropdown the user could still open. It's still identified as
  // `[data-component-id="Button"]` in tests/queries: the underlying `Button`
  // component hardcodes that attribute after its own `...rest` spread, so a
  // custom `data-component-id` passed here is silently overridden - don't add
  // one, it won't take effect.
  const caret =
    readOnly || (hideCaretOnDisable && disabled) ? null : (
      <S.IconWrapper
        forwardedAs="span"
        $isDisabled={!!disabled}
        $isOpen={isOpen}
        $size={size}
        use={hasError ? 'ghost-error' : 'ghost'}
        size="unset"
        // Note: the searchable trigger opens on focus/click, which alone can't close it - so its
        // caret toggles explicitly, like the non-searchable trigger's button: opening focuses the
        // input, closing blurs it. Stopping the click keeps the <label> from also forwarding it to
        // the input (and the reference from reopening).
        onClick={
          searchable
            ? (e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                if (disabled) return
                if (isOpen) {
                  onToggleOpen(false)
                  // Note: closing via the caret ends the interaction - drop focus too, so the
                  // field reads as closed (placeholder, caret) and typing doesn't reopen it.
                  searchInputRef.current?.blur()
                } else {
                  searchInputRef.current?.focus()
                  onToggleOpen(true)
                }
              }
            : undefined
        }
      >
        <Icon icon={CaretDownIcon} size="1.6rem" />
      </S.IconWrapper>
    )

  // Note: a `span` like the caret (a `<button>` can't nest inside the trigger
  // button). Stops the click/press from reaching the trigger, so clearing never
  // toggles the dropdown or (searchable) focuses the input.
  const clearButton = onClear && selectedCount > 0 && !disabled && !readOnly && (
    <S.IconWrapper
      forwardedAs="span"
      role="button"
      aria-label="Clear"
      $isDisabled={false}
      $isOpen={false}
      $size={size}
      use={hasError ? 'ghost-error' : 'ghost'}
      size="unset"
      onMouseDown={(e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        onClear()
      }}
    >
      <Icon icon={XIcon} size="1.6rem" />
    </S.IconWrapper>
  )

  const trailing = (caret || clearButton || trailingCount) && (
    <S.TrailingGroup>
      {caret}
      {clearButton}
      {trailingCount}
    </S.TrailingGroup>
  )

  if (searchable) {
    return (
      <S.SelectSearchBox
        ref={setReference}
        $hasBadges={hasBadges}
        $hasError={hasError}
        $isDisabled={disabled}
        $isReadOnly={readOnly}
        $size={size}
        htmlFor={id}
        {...referenceProps}
        // Note: a press on anything but the input itself (caret, padding, badges) would blur the
        // focused input, and the trigger closes on blur - then the label forwards the click back to
        // the input and it reopens, so one click did close + open. Keep focus where it is.
        onMouseDown={(e: React.MouseEvent<HTMLLabelElement>) => {
          if (e.target !== searchInputRef.current) e.preventDefault()
          ;(referenceProps.onMouseDown as React.MouseEventHandler | undefined)?.(e)
        }}
      >
        {leadingSlot}
        {hasBadges ? (
          <>
            {badgesArea}
            <S.HiddenSearchLayer>
              <S.SearchTriggerInput
                ref={searchInputRef}
                $hasError={hasError}
                $size={size}
                id={id}
                disabled={disabled}
                readOnly={readOnly}
                type="text"
                autoComplete="off"
                aria-invalid={hasError || undefined}
                aria-describedby={describedBy}
                placeholder={
                  readOnly ? undefined : isOpen ? (searchPlaceholder ?? 'Search...') : placeholder
                }
                value={isOpen ? searchQuery : closedSearchValue}
                onChange={onSearchChange}
                onBlur={onSearchBlur}
              />
            </S.HiddenSearchLayer>
          </>
        ) : (
          <S.SelectButtonContent $isMulti={isMulti}>
            <S.SearchTriggerInput
              ref={searchInputRef}
              $hasError={hasError}
              $size={size}
              id={id}
              disabled={disabled}
              readOnly={readOnly}
              type="text"
              autoComplete="off"
              aria-invalid={hasError || undefined}
              aria-describedby={describedBy}
              placeholder={
                readOnly ? undefined : isOpen ? (searchPlaceholder ?? 'Search...') : placeholder
              }
              value={isOpen ? searchQuery : closedSearchValue}
              onChange={onSearchChange}
              onBlur={onSearchBlur}
            />
            {selectedStatus && !isOpen && (
              <S.TriggerStatusBadge
                size="tiny"
                color={selectedOptions[0]?.statusColor ?? 'neutral'}
                variant="light"
              >
                {selectedStatus}
              </S.TriggerStatusBadge>
            )}
          </S.SelectButtonContent>
        )}
        {loading && <BorderLoader size="small" />}
        {trailing}
      </S.SelectSearchBox>
    )
  }

  return (
    <S.SelectButton
      ref={setReference}
      $hasBadges={hasBadges}
      $hasError={hasError}
      $isDisabled={disabled}
      $isReadOnly={readOnly}
      $size={size}
      disabled={disabled}
      id={id}
      type="button"
      aria-invalid={hasError || undefined}
      aria-describedby={describedBy}
      {...referenceProps}
    >
      {leadingSlot}
      {hasBadges ? (
        badgesArea
      ) : (
        <S.SelectButtonContent $isMulti={isMulti}>
          <SelectTruncationTooltip label={triggerLabel}>
            {(truncationProps) => (
              <S.SelectButtonText
                {...truncationProps}
                size="unset"
                $hasError={hasError}
                $isDisabled={disabled}
                $isPlaceholder={!selectedCount}
                $size={size}
                breakLines={1}
              >
                {triggerLabel}
              </S.SelectButtonText>
            )}
          </SelectTruncationTooltip>
          {selectedStatus && (
            <S.TriggerStatusBadge
              size="tiny"
              color={selectedOptions[0]?.statusColor ?? 'neutral'}
              variant="light"
            >
              {selectedStatus}
            </S.TriggerStatusBadge>
          )}
        </S.SelectButtonContent>
      )}
      {loading && <BorderLoader size="small" />}
      {trailing}
    </S.SelectButton>
  )
}
